/**
 * Swedish user-facing messages for each AppErrorCode (CLIENT-origin codes only).
 *
 * SERVER-origin errors always show a generic message + the error code so the
 * user can refer to it when contacting support.
 *
 * Keys are the MIMIR-XXXX strings returned in the `errorCode` field of
 * the backend ErrorResponse.
 */
export const APP_ERROR_MESSAGES = {
  // 1xxx — Auth & Behörighet
  'MIMIR-1001': 'Din inloggning är ogiltig. Logga ut och logga in igen.',
  'MIMIR-1002': 'Din session har gått ut. Logga in igen.',
  'MIMIR-1003': 'Autentiseringsfel. Kontakta administratör.',
  'MIMIR-1004': 'Okänd inloggningskälla. Kontakta administratör.',
  'MIMIR-1005': 'Du saknar behörighet för denna åtgärd.',
  'MIMIR-1006': 'Du har inte behörighet till denna organisation.',
  'MIMIR-1007': 'Du har inte behörighet att ändra denna testkörning.',
  'MIMIR-1008': 'Du har inte behörighet att ändra detta testresultat.',

  // 2xxx — Validering
  // MIMIR-2001 hanteras av backend med specifika fältmeddelanden — visas via HTTP 400-fallback
  'MIMIR-2002': 'Kontraktet kunde inte hittas.',
  'MIMIR-2003': 'Testkörningen kunde inte hittas.',
  'MIMIR-2004': 'Organisationen kunde inte hittas.',
  'MIMIR-2005': 'Användaren kunde inte hittas.',
  'MIMIR-2006': 'Testresultatet kunde inte hittas.',
  'MIMIR-2007': 'Beställningen eller den angivna Jira-nyckeln hittades inte. Kontrollera angiven information och försök igen.',
  'MIMIR-2008': 'Begäran innehåller ogiltig JSON. Kontrollera datan och försök igen.',
  'MIMIR-2009': 'Ogiltigt filnamn — åtkomst nekad.',
  'MIMIR-2010': 'Filtypen är inte tillåten. Endast .json, .html och .xml accepteras för kontrakt, och .docx för dokumentimport.',
  'MIMIR-2011': 'Filen är för stor — max 10 MB.',
  'MIMIR-2012': 'Word-filen kunde inte läsas. Kontrollera att den är ett giltigt .docx-dokument.',
  'MIMIR-2013': 'Det valda dokumentet tillhör inte detta tjänstekontrakt.',
  'MIMIR-2014': 'Dokumentets version stämmer inte med den valda tjänstekontraktsversionen.',

  // 3xxx — Konflikter
  'MIMIR-3001': 'E-postadressen är redan registrerad. Välj en annan e-postadress.',
  'MIMIR-3002': 'En resurs med detta namn eller ID finns redan, eller befinner sig i ett ogiltigt tillstånd. Kontrollera informationen och försök igen.',
  'MIMIR-3003': 'Användarnamnet är redan registrerat. Välj ett annat användarnamn.',
  'MIMIR-3004': 'Någon annan har ändrat uppgifterna medan du arbetade. Ladda om sidan för att se den senaste versionen.',
  'MIMIR-3005': 'Statusen kan inte ändras eftersom ärendet redan har behandlats. Ladda om sidan.',
  'MIMIR-3006': 'Ärendet är redan taget av en annan granskare.',

  // 4xxx — SoapUI / Testkörning (CLIENT-kod)
  'MIMIR-4009': 'En assertion i testfallet misslyckades — svaret matchar inte förväntat värde.',

  // E2E — 1xxx Auth & Behörighet
  'E2E-1001': 'Du saknar behörighet.',
  'E2E-1002': 'Du har inte behörighet till denna inlämning.',
  'E2E-1003': 'Du har inte behörighet till denna bilaga.',
  'E2E-1004': 'Du har inte behörighet till denna kommentar.',
  'E2E-1005': 'Granskaren tillhör inte någon av dina avdelningar.',

  // E2E — 2xxx Resurser saknas
  'E2E-2001': 'Formuläret kunde inte hittas.',
  'E2E-2002': 'Inlämningen kunde inte hittas.',
  'E2E-2003': 'Sektionen kunde inte hittas.',
  'E2E-2004': 'Frågan kunde inte hittas.',
  'E2E-2005': 'Bilagan kunde inte hittas.',
  'E2E-2006': 'Kommentaren kunde inte hittas.',
  'E2E-2007': 'Granskningen kunde inte hittas.',
  'E2E-2008': 'Svarsalternativet kunde inte hittas.',
  'E2E-2009': 'Svaret kunde inte hittas.',
  'E2E-2010': 'Användaren kunde inte hittas.',
  'E2E-2011': 'Avdelningen kunde inte hittas.',

  // E2E — 3xxx Tillståndskonflikter
  'E2E-3001': 'Inlämningen kan inte ändras — den är inte längre ett utkast.',
  'E2E-3002': 'Granskare är redan tilldelad denna inlämning.',
  'E2E-3003': 'Inlämningen befinner sig i ett ogiltigt tillstånd för denna åtgärd.',
  'E2E-3004': 'Ogiltigt granskningsbeslut.',
  'E2E-3005': 'Avdelningskoden är redan registrerad. Välj en annan kod.',
  'E2E-3006': 'Avdelningen kunde inte sparas — kontrollera kod och namn.',
  'E2E-3007': 'Svaren kan bara ändras när granskaren har begärt förtydligande.',
  'E2E-3008': 'Frågan är inte öppen för ändring — bara frågor med kommentarer kan redigeras.',

  // E2E — 4xxx Filvalidering
  'E2E-4001': 'Ogiltigt filformat — endast .zip-filer tillåts.',
  'E2E-4002': 'Filen är för stor — max 10 MB.',
  'E2E-4003': 'Filinnehållet är ogiltigt — filen verkar inte vara ett riktigt ZIP-arkiv.',

  // 7xxx — Rate Limiting
  'MIMIR-7001': 'För många förfrågningar från din anslutning. Vänta en stund och försök igen.',
  'MIMIR-7002': 'För många förfrågningar. Vänta en stund och försök igen.',
  'MIMIR-7003': 'För många AI-förfrågningar. Vänta en stund och försök igen.',
};

/**
 * Returns a user-facing Swedish message for an error code.
 *
 * - CLIENT codes   → specific message from the lookup table above
 * - SERVER codes   → generic text (the code itself is shown separately in the UI)
 * - Unknown code   → null (caller falls back to HTTP-status message)
 *
 * @param {string} errorCode  e.g. "MIMIR-2003"
 * @param {string} origin     "CLIENT" | "SERVER"
 * @returns {string|null}
 */
export const getErrorMessage = (errorCode, origin) => {
  if (!errorCode) return null;
  if (origin === 'SERVER') return 'Ett tekniskt fel inträffade. Kontakta oss på mimir@inera.se';
  return APP_ERROR_MESSAGES[errorCode] ?? null;
};
